/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Static Assets Manifest, Helpers & Brand Metadata
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import {
  SOPHIA_DEFAULT_ICONS_DATA,
  type SophiaDefaultIconName,
  type SophiaIconCategory,
  type SophiaIconData,
} from './icons-data';

// ============================================================================
// Brand Tokens & Metadata
// ============================================================================

/**
 * AI Provider brand color hex codes for consistent UI badge and card rendering.
 */
export const AI_PROVIDER_BRAND_COLORS: Record<string, string> = {
  openai: '#10A37F',
  anthropic: '#D97757',
  gemini: '#1A73E8',
  deepseek: '#0066FF',
  fal: '#FF5E57',
  kling: '#7C3AED',
  elevenlabs: '#0F172A',
  stability: '#8B5CF6',
  midjourney: '#2563EB',
  modelslab: '#EC4899',
  stripe: '#635BFF',
  paypal: '#0079C1',
  paddle: '#00D26A',
} as const;

/**
 * Returns the hex brand color for a given AI provider or payment gateway.
 */
export function getBrandColor(providerCode: string): string {
  return AI_PROVIDER_BRAND_COLORS[providerCode.toLowerCase()] ?? '#6366F1';
}

/**
 * List of available icon names in the asset bundle.
 */
export const AVAILABLE_ICONS = [
  'openai',
  'anthropic',
  'gemini',
  'deepseek',
  'fal',
  'kling',
  'elevenlabs',
  'stability',
  'modelslab',
  'stripe',
  'paypal',
  'paddle',
  'sparkles',
  'wand',
  'chat',
] as const;

export type IconName = (typeof AVAILABLE_ICONS)[number];

/**
 * Logo variants supported across landing pages and workspaces.
 */
export const LOGO_VARIANTS = {
  mark: '/assets/images/logo-mark.svg',
  fullLight: '/assets/images/logo-full-light.svg',
  fullDark: '/assets/images/logo-full-dark.svg',
} as const;

/**
 * Typography font family tokens.
 */
export const FONT_TOKENS = {
  sans: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  heading: 'Outfit, sans-serif',
  mono: '"JetBrains Mono", monospace',
} as const;

// ============================================================================
// Sophia Default Icons Library (Zero-Bundle-IO Edge SVG Assets)
// ============================================================================

export * from './icons-data';
export * from './components/SophiaIcon';

/**
 * Get icon metadata and SVG payload by icon name.
 */
export function getSophiaIconData(name: string): SophiaIconData | undefined {
  return SOPHIA_DEFAULT_ICONS_DATA[name as SophiaDefaultIconName];
}

/**
 * Retrieve all default icons belonging to a specific functional category.
 */
export function getSophiaIconsByCategory(category: SophiaIconCategory): SophiaIconData[] {
  return Object.values(SOPHIA_DEFAULT_ICONS_DATA).filter(
    (icon) => icon.category === category
  );
}

/**
 * Search default icons by name, Vietnamese translation, or tags.
 */
export function searchSophiaIcons(query: string): SophiaIconData[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return Object.values(SOPHIA_DEFAULT_ICONS_DATA);
  }

  return Object.values(SOPHIA_DEFAULT_ICONS_DATA).filter((icon) => {
    return (
      icon.name.toLowerCase().includes(normalized) ||
      icon.nameVi.toLowerCase().includes(normalized) ||
      icon.tags.some((tag) => tag.toLowerCase().includes(normalized)) ||
      icon.category.toLowerCase().includes(normalized)
    );
  });
}
